// @ts-nocheck

import {
  formatNumber as formatSharedNumber,
  getCurrencySymbol as getSharedCurrencySymbol,
} from '@metapic-apps/shared'
import moment from 'moment'

import { normalizeImage } from '@/lib/adapters'
import { normalizeSocialItem } from '@/lib/adapters/creators.adapter'
import { formatMoneyMinorForAdvertiser } from '@/lib/formatters/money.formatter'

const normalizeLogoData = (file) => {
  const formData = new FormData()
  formData.append('logo', file)

  return formData
}

const validateInteger = (value) => Number.isInteger(Number(value))

const numberMinimiseFormat = (value) =>
  formatSharedNumber(value, 'compact', 'decimal', 0, currencyLocale)

const fallbackLang = 'en' // Should never be used but should prevent crashes in case navigator.language fails

// Default before store is set
let currencyCode = 'EUR'
let currencyLocale = navigator.language || fallbackLang
const formatMoney = (cost) =>
  formatMoneyMinorForAdvertiser({ amount: cost, currency: currencyCode }, { allowZero: true })

const getCurrencySymbol = () => getSharedCurrencySymbol(currencyCode, currencyLocale)

const formatNumber = (number) =>
  formatSharedNumber(number, 'standard', 'decimal', 3, currencyLocale)

const formatFractionAsPercentage = (percentageAsFraction) =>
  formatNumber(percentageAsFraction * 100) + '%'

const formatFractionAsPercentageWithoutSign = (percentageAsFraction) =>
  formatSharedNumber(Math.round(percentageAsFraction * 100), 'standard', 'decimal', 0, 'en-US')

const formatDomainBeforeSend = (domain) => {
  return domain
    .replace(/www./gi, '')
    .replace(/https:\/\//gi, '')
    .replace(/http:\/\//gi, '')
}

export const convertMinorAmountToMajor = (cost: number | string): number => {
  if (typeof cost === 'string') {
    return Number.parseFloat(cost) / 100
  }
  return cost / 100
}

const checkSameCosts = (baseCosts, newCosts) => {
  let same = true
  try {
    newCosts.forEach((el) => {
      const source = el.source
      const compEl = baseCosts.find((el) => el.source === source)
      if (el.cpc !== compEl.cpc) {
        same = false
      }
      if (el.cpa !== compEl.cpa) {
        same = false
      }
    })
    return same
  } catch {
    same = false
    return same
  }
}

const normalizeStore = (data) => {
  const normalizeDomains = (domains) => {
    return domains.map((domain, i) => {
      return {
        id: i,
        text: domain.end_host,
      }
    })
  }

  if (data.language_iso_code && data.currency_iso_code) {
    currencyCode = data.currency_iso_code
    currencyLocale = data.language_iso_code
  }

  const traffic_sources_costs_formatted = data.traffic_sources_costs
  if (traffic_sources_costs_formatted.length) {
    traffic_sources_costs_formatted.map((el) => {
      el.type = el.cpa ? 'CPA' : 'CPC'
      if (el.cpc) {
        el.cpc = Number((Number(el.cpc).toFixed(0) / 100).toFixed(2))
      }
      if (el.invoice_cpc) {
        el.invoice_cpc = Number((Number(el.invoice_cpc).toFixed(0) / 100).toFixed(2))
      }
      if (el.cpa) {
        el.cpa = Number((Number(el.cpa).toFixed(2) * 100).toFixed(0))
      }
      if (el.invoice_cpa) {
        el.invoice_cpa = Number((Number(el.invoice_cpa).toFixed(2) * 100).toFixed(0))
      }
      if (el.cpc === null && el.cpa === null) {
        el.cpc = 0
      }
    })
  }

  let user_permissions = false
  if (data.user_permissions?.length) {
    user_permissions = data.user_permissions[0]
      ? data.user_permissions[0].editable_client_id !== null
      : false
  }

  return {
    name: data.feed_name,
    id: data.id,
    card: data.card,
    clearingNumber: null,
    trafficSources: traffic_sources_costs_formatted,
    domains: normalizeDomains(data.urls),
    storeGroupId: data.store_group_id,
    storeGroups: data.store_groups,
    image: data.logo ? data.logo : { url: data.logo_url },
    originalLogo: data.logo_url,
    categories: data.store_categories.map((item) => item.id),
    hidden: data.hidden,
    advertiserUsesCpa: Boolean(data.advertiser_use_cpa),
    userCanEditAllInfluencers: user_permissions,
    currency: data.currency_obj,
    locale: data.locale,
    paymentType: data.payment_type,
    stripeSubscriptionStatus: data.stripe_subscription_status,
    stripCustomerId: data.stripe_customer_id,
    isDirectStore: data.is_direct,
    email: data.invoice_config?.emails?.[0],
    companyName: data.invoice_config?.company_name,
    isWhiteLabel: Boolean(data.user_permissions[0]?.editable_client_id),
    createdAt: data.created_at,
    config: data.user_permissions?.[0]?.config,
  }
}

export const normalizeChannelInfluencers = (data) => {
  const users = []
  data.forEach((user) => {
    const normalizedUser = {}
    normalizedUser.id = user.user_id
    normalizedUser.username = user.username
    normalizedUser.userTags = user.user_tags
    normalizedUser.instagramFollowers = user.instagram_followers
    normalizedUser.clicks = user.clicks
    normalizedUser.cost = convertMinorAmountToMajor(user.store_cost)
    if ('order_value' in user) {
      normalizedUser.orderValue = convertMinorAmountToMajor(user.order_value)
    }
    if ('nr_orders' in user) {
      normalizedUser.nrOfOrders = user.nr_orders
    }
    if ('roas' in user) {
      normalizedUser.roas = user.roas && user.roas !== 'inf' ? Number(user.roas).toFixed(2) : null
    }
    if ('traffic_sources_costs' in user) {
      normalizedUser.traffic_sources_costs = user.traffic_sources_costs
        ? Object.values(user.traffic_sources_costs)
        : []
    }
    users.push(normalizedUser)
  })
  return users
}

export const normalizeLinks = (data) => {
  return data.map((link) => ({
    originalUrl: link.original_url,
    text: link.text,
    tagId: link.tag_id,
    userId: link.user_id,
    username: link.username,
    clicks: link.clicks,
    cost: convertMinorAmountToMajor(link.cost),
    image: link.image ? link.image : link.img ? { key: link.img } : undefined,
    title: link.title,
    costPerClick: convertMinorAmountToMajor(link.cost_per_click),
    orderValue: convertMinorAmountToMajor(link.order_value),
    nrOfOrders: link.nr_orders,
    createdAt: link.created_at,
  }))
}

export const normalizePosts = (data) => {
  return data.map((post) => ({
    ...post,
    originalUrl: post.original_url,
    cost: convertMinorAmountToMajor(post.cost),
    order_value: convertMinorAmountToMajor(post.order_value),
  }))
}

export const normalizeInfluencer = (data) => {
  const traffic_sources_costs_formatted = data.traffic_sources_costs
  if (traffic_sources_costs_formatted.length) {
    traffic_sources_costs_formatted.map((el) => {
      el.type = el.cpa ? 'CPA' : 'CPC'
      if (el.cpc) {
        el.cpc = Number((Number(el.cpc).toFixed(0) / 100).toFixed(2))
      }
      if (el.invoice_cpc) {
        el.invoice_cpc = Number((Number(el.invoice_cpc).toFixed(0) / 100).toFixed(2))
      }
      if (el.cpa) {
        el.cpa = Number((Number(el.cpa).toFixed(2) * 100).toFixed(0))
      }
      if (el.invoice_cpa) {
        el.invoice_cpa = Number((Number(el.invoice_cpa).toFixed(2) * 100).toFixed(0))
      }
    })
  }
  return {
    userId: data.id,
    username: data.username,
    email: data.email,
    firstName: data.first_name,
    surname: data.surname,
    userTags: data.user_tags.map((t) => t.name),
    userImage: data.user_image ? normalizeImage(data.user_image) : undefined,
    socialMediaAccounts: data.social_media.map(normalizeSocialItem),
    myPageSlug: data.my_page_slug ?? null,
    oneTimeEarnings: data.one_time_earnings.map((earning) => ({
      amount: convertMinorAmountToMajor(earning.user_earnings),
      paymentDate: earning.payment_date,
      comment: earning.comment,
    })),
    trafficSources: traffic_sources_costs_formatted,
  }
}

export const normalizeStats = (data) => {
  const perSourcePerDay = {}
  Object.keys(data.per_source).forEach((key) => {
    const sourceData = data.per_source[key]
    perSourcePerDay[key] = sourceData.map((stats) => ({
      clicks: stats.clicks,
      cost: convertMinorAmountToMajor(stats.cost),
      nrOfOrders: stats.nr_orders,
      orderValue: convertMinorAmountToMajor(stats.order_value),
      date: stats.date,
    }))
  })
  return {
    clicks: data.clicks,
    cost: convertMinorAmountToMajor(data.cost),
    avgOrderValue: convertMinorAmountToMajor(data.avg_order_value),
    nrOfOrders: data.nr_orders,
    orderValue: convertMinorAmountToMajor(data.order_value),
    uniqueLinks: data.unique_links,
    uniqueUsers: data.unique_users,
    perDay: data.per_day.map((stats) => ({
      clicks: stats.clicks,
      cost: convertMinorAmountToMajor(stats.cost),
      nrOfOrders: stats.nr_orders,
      orderValue: convertMinorAmountToMajor(stats.order_value),
      date: stats.date,
    })),
    perSourcePerDay,
  }
}

const getDateToSaveFromDateRange = (dateRange) => {
  const dateRangeMoment = {
    start: moment(dateRange.start),
    end: moment(dateRange.end),
  }

  const entryWithShortcut = Object.entries(dateShortcutToDateRange).find(([, dateRange]) =>
    dateRangeMomentIsEqual(dateRangeMoment, dateRange),
  )
  return entryWithShortcut ? entryWithShortcut[0] : dateRange
}

const dateRangeMomentIsEqual = (dateRange1, dateRange2) => {
  return (
    dateRange1.start.isSame(dateRange2.start, 'day') && dateRange1.end.isSame(dateRange2.end, 'day')
  )
}

const dateShortcutToDateRange = {
  'This Year': { start: moment().startOf('year'), end: moment() },
  'Last Year': {
    start: moment().subtract(1, 'year').startOf('year'),
    end: moment().subtract(1, 'year').endOf('year'),
  },
  'This Month': { start: moment().startOf('month'), end: moment() },
  'Last Month': {
    start: moment().subtract(1, 'month').startOf('month'),
    end: moment().subtract(1, 'month').endOf('month'),
  },
  'Last 30 Days': { start: moment().subtract(30, 'days'), end: moment() },
  'Last 7 Days': { start: moment().subtract(7, 'days'), end: moment() },
}

const formatNumberInKilo = (value) => {
  if (value < 1e3) {
    return value.toFixed(1)
  }
  if (value >= 1e3 && value < 1e6) {
    return +(value / 1e3).toFixed(1) + 'K'
  }
  if (value >= 1e6 && value < 1e9) {
    return +(value / 1e6).toFixed(1) + 'M'
  }
  if (value >= 1e9 && value < 1e12) {
    return +(value / 1e9).toFixed(1) + 'B'
  }
  if (value >= 1e12) {
    return +(value / 1e12).toFixed(1) + 'T'
  }
}

const normalizeUser = (data) => {
  return {
    ...data,
    ...(data.stores?.length > 0 && {
      stores: data.stores.map(normalizeStoreCommonData),
    }),
  }
}

const normalizeStoreCommonData = (storeData) => {
  if (!storeData) {
    return null
  }
  return {
    ...storeData,
    image: { url: storeData.logo_url },
    config: storeData.config,
    stripeSubscriptionStatus: storeData.stripe_subscription_status,
    name: storeData.feed_name,
  }
}

export {
  checkSameCosts,
  formatDomainBeforeSend,
  formatFractionAsPercentage,
  formatFractionAsPercentageWithoutSign,
  formatMoney,
  formatNumber,
  formatNumberInKilo,
  getCurrencySymbol,
  getDateToSaveFromDateRange,
  normalizeLogoData,
  normalizeStore,
  normalizeStoreCommonData,
  normalizeUser,
  numberMinimiseFormat,
  validateInteger,
}
